import { useAppSelector } from "@/store/hooks";
import { RootState, store } from "@/store/store";
import { Device, SingleDeviceReorderPayload, SparePartsReorderPayload } from "@/types/devices";
import DOMPurify from 'dompurify';

export function decodeHtmlCharCodes(str: string) {
  if (str) {
    str = str.replace(/(&#(\d+);)|(&(\w+);)/g, (match, capture, charCode) => String.fromCharCode(charCode));
    return str;
  }
}

export function truncateText(text: string, size: number) {
  if (text && size) {
    return text.length > (size + 2) ? text.substring(0, size) + "..." : text;
  }
}

/**
 * Sanitizes base64 image sources to prevent XSS attacks
 * @param base64 - The base64 image string to sanitize
 * @returns Sanitized image source string
 */
export const SafeImageSrc = (base64: string) => {
    const sanitizedSrc = DOMPurify.sanitize(
        base64,
        { ALLOWED_URI_REGEXP: /^data:image\/(png|jpeg|jpg|gif);base64,/ }
    );
    return sanitizedSrc;
};

/**
 * Formats error message from API error response
 * @param error - The error object from API call
 * @param defaultMessage - Default message to show if no error message found
 * @returns Formatted error message string
 */
export const formatErrorMessage = (error: unknown, defaultMessage: string = 'An error occurred'): string => {
  if (!error) return defaultMessage;

  const err = error as Error & {
    response?: {
      data?: {
        error_message?: string;
        message?: string;
        error?: string;
      }
    }
  };

  return err?.response?.data?.error_message
    || err?.response?.data?.message
    || err?.response?.data?.error
    || err?.message
    || defaultMessage;
};


export const getPayloadForSpareParts = (device: Device): SparePartsReorderPayload => {
  if (device.spareParts && device.spareParts.length > 0) {
    return {
      cartItems: device.spareParts.map((part) => ({
        sku: part?.stock_keeping_unit || '',
        qty: part.quantity,
        tagReference:device?.tagNumber || '',
        productDtls: {
          productType: 'Spare Parts'
        }
      }))
    };
  }
  throw new Error('No spare parts available for the device');
};

export const getPayloadForSingleDeviceReOrder = (device: Device): SingleDeviceReorderPayload => {
  return {
    cartItems: [
      {
        sku: device.stock_keeping_unit,
        qty: "1",
        modelString: device.modelNumber,
        tagReference:device?.tagNumber || '',
        productDtls: {
          productType: 'Base model'
        }
      }
    ]
  };
};

export const sanitizeFilenameForDownload = (filename: string): string => {
  if (!filename || typeof filename !== 'string') return 'downloaded_file';

  return filename
    .replace(/[/\\]/g, '') // Remove path separators
    .replace(/[<>:"|?*]/g, '') // Remove filesystem dangerous characters
    .replace(/[\u0000-\u001f]/g, '') // Remove control characters
    .replace(/^\.+/, '') // Remove leading dots
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .trim()
    .slice(0, 255) || 'downloaded_file'; // Limit length and provide fallback
};

export function addTargetBlank(html: string) {
    return html.replace(/<a(?![^>]*target=)/g, '<a target="_blank" rel="noopener noreferrer"');
}
